import { useAtom } from 'jotai';
import { z } from 'zod';

import {
	addressSchema,
	createLog,
	legalNameSchema,
	LogSeverity,
	taxNumberSchema,
} from '@ekhodealer/ekho-common';
import { Box, Form, Stack } from '@ekhodealer/ekho-common/rev';

import type { Address } from '@ekhodealer/ekho-common';

import { SettingsSection } from '@common/components/SettingsSection';
import { SettingsPageContent } from '@common/components/SettingsTableOfContents';
import { createTocItem } from '@common/components/settingsTocUtils';
import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdateBusinessDetails } from '../../api/configuration/business-details/useUpdateBusinessDetails';
import { forceValidateBankAccountDetailsAtom } from '../../atoms/onboardingValidationAtoms';

import BankAccountOnboarding from './BankAccountOnboarding';

/** Payable fields that exist on the underlying data but not on the base ServicesProvider class. */
interface PayableFields {
	taxNumber?: string;
	legalName?: string;
	legalAddress?: Address | null;
}

function getPayableFields(
	servicesProvider: { toObject?: () => Record<string, unknown> } | undefined | null,
): PayableFields {
	if (!servicesProvider) return {};
	return (servicesProvider.toObject?.() ?? servicesProvider) as unknown as PayableFields;
}

function getTaxLegalNameDefaults(servicesProvider: { toObject?: () => Record<string, unknown> } | undefined | null): {
	taxNumber: string;
	legalName: string;
} {
	const data = getPayableFields(servicesProvider);
	return {
		taxNumber: data.taxNumber || '',
		legalName: data.legalName || '',
	};
}

function getLegalAddressDefaults(servicesProvider: { toObject?: () => Record<string, unknown> } | undefined | null): {
	legalAddress: Address | null;
} {
	const data = getPayableFields(servicesProvider);
	return {
		legalAddress: data.legalAddress || null,
	};
}

const taxLegalNameSchema = z.object({
	taxNumber: taxNumberSchema,
	legalName: legalNameSchema,
});

const legalAddressSchema = z.object({
	legalAddress: addressSchema.nullable().optional(),
});

type TaxLegalNameFormData = z.infer<typeof taxLegalNameSchema>;
type LegalAddressFormData = z.infer<typeof legalAddressSchema>;

// ---------------------------------------------------------------------------
// Copy rationale (from code trace, March 2025):
//
// These fields live on the PayableServicesProvider class and were originally
// bundled under "bank account" because payouts require a legal entity. However,
// tracing through the backend reveals they are primarily used on sale documents,
// not for banking:
//
//   - taxNumber (EIN): Only used on the bill of sale (dealerTaxNumber field in
//     billOfSale.ts). NOT used in the payout/Brex flow at all.
//
//   - legalName: Used as the seller/dealer name on ALL sale documents (bill of
//     sale, purchase agreement, titling, registration, MSO) AND as company_name
//     when creating the Brex payout vendor.
//
//   - legalAddress: Used as the dealer address on sale documents (purchase
//     agreement, titling, MSO) via PayableServicesProvider.getAddress(). Also
//     used by getSellerState() for tax/regulatory state determination. NOT used
//     in the payout/Brex flow.
//
// TODO: Consider moving taxNumber, legalName, and legalAddress out of the bank
// account settings page and into Business Details, since they are fundamentally
// business entity information used on legal sale documents — not bank-account-
// specific data. The only field with a real bank account tie is legalName (used
// as the Brex vendor company_name). The current placement is a historical
// artifact of the PayableServicesProvider bundling.
// ---------------------------------------------------------------------------

const BANK_ACCOUNT_TOC = [
	createTocItem('Connect your bank'),
	createTocItem('Legal entity details'),
];

const CONNECT_BANK_DESCRIPTION = 'Link your bank account to receive payouts from orders.';

export function BankAccountForm(): React.ReactNode {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const updateBusinessDetailsMutation = useUpdateBusinessDetails();
	const [forceValidation, setForceValidation] = useAtom(forceValidateBankAccountDetailsAtom);

	const handleTaxLegalNameSubmit = async (data: TaxLegalNameFormData): Promise<{ isSuccess: boolean }> => {
		if (!servicesProvider) return { isSuccess: false };

		try {
			const currentData = getPayableFields(servicesProvider);
			await updateBusinessDetailsMutation.mutateAsync({
				servicesProvider: {
					taxNumber: data.taxNumber,
					legalName: data.legalName,
					legalAddress: currentData.legalAddress,
				} as Record<string, unknown>,
			});
			setForceValidation(false);
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update tax & legal name: ${error instanceof Error ? error.message : String(error)}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	const handleLegalAddressSubmit = async (data: LegalAddressFormData): Promise<{ isSuccess: boolean }> => {
		if (!servicesProvider) return { isSuccess: false };

		try {
			const currentData = getPayableFields(servicesProvider);
			await updateBusinessDetailsMutation.mutateAsync({
				servicesProvider: {
					taxNumber: currentData.taxNumber || '',
					legalName: currentData.legalName || '',
					legalAddress: data.legalAddress,
				} as Record<string, unknown>,
			});
			setForceValidation(false);
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update legal business address: ${error instanceof Error ? error.message : String(error)}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	return (
		<SettingsPageContent tocItems={BANK_ACCOUNT_TOC} stickyTop={32}>
			<SettingsSection
				title='Connect your bank'
				description={CONNECT_BANK_DESCRIPTION}>
				<BankAccountOnboarding servicesProviderId={servicesProvider?.id || ''} />
			</SettingsSection>
			<SettingsSection
				title='Legal entity details'
				description="Your registered business information. This is used as the seller details on sale documents (bill of sale, purchase agreements, titling) and to identify your business for payouts."
				hideDivider>
				<Stack itemsSpacing='16'>
					<Box padding='24' background='secondary' rounding='12'>
						<Form
							name='bank-account-tax-legal-name'
							schema={taxLegalNameSchema}
							defaultValues={getTaxLegalNameDefaults(servicesProvider)}
							onSubmit={handleTaxLegalNameSubmit}
							forceValidation={forceValidation}
							title='Tax & legal name'
							titleLevel='subgroup'
							titleWeight='semibold'
							showSaveAndClear>
							<Form.Field.Text
								schemaField='taxNumber'
								labelText='Federal Tax ID (EIN)'
								descriptionText='Your Federal Employer Identification Number. This appears on sale documents such as the bill of sale.'
								required
								size='large'
								placeholder='12-3456789'
							/>
							<Form.Field.Text
								schemaField='legalName'
								labelText='Legal business name'
								descriptionText="Your registered business entity name. This appears as the seller name on all sale documents and is used to identify your business for payouts."
								required
								size='large'
								placeholder='Enter legal business name'
							/>
						</Form>
					</Box>
					<Box padding='24' background='secondary' rounding='12'>
						<Form
							name='bank-account-legal-address'
							schema={legalAddressSchema}
							defaultValues={getLegalAddressDefaults(servicesProvider)}
							onSubmit={handleLegalAddressSubmit}
							forceValidation={forceValidation}
							title='Legal business address'
							titleLevel='subgroup'
							titleWeight='semibold'
							showSaveAndClear>
							<Form.Field.Address
								schemaField='legalAddress'
								labelText='Legal business address'
								descriptionText='Your registered business address. This appears as the dealer address on sale documents such as purchase agreements and titling paperwork.'
								placeholder='Start typing address'
								size='large'
							/>
						</Form>
					</Box>
				</Stack>
			</SettingsSection>
		</SettingsPageContent>
	);
}
